import { useState, useEffect, useRef } from "react";
import Logo from "@/components/base/Logo";

const mainLinks = [
  { label: "Strona główna", path: "/" },
  { label: "O mnie", path: "/o-mnie" },
  { label: "Oferta", path: "/oferta" },
  { label: "Dla firm", path: "/dla-firm" },
];

const toolsLinks = [
  { label: "Expert Kredytowy", path: "/doradca360" },
  { label: "Kalkulator nadpłaty", path: "/narzedzia/nadplata-kredytu" },
  { label: "Kalkulator refinansowania", path: "/narzedzia/refinansowanie" },
  { label: "Kalkulator zdolności", path: "/narzedzia/zdolnosc-kredytowa" },
];

const otherLinks = [
  { label: "Jak działam", path: "/jak-dzialam" },
  { label: "Analiza", path: "/analiza" },
  { label: "Blog", path: "/blog" },
  { label: "FAQ", path: "/faq" },
  { label: "Kontakt", path: "/kontakt" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeNavigation = () => {
    setMobileOpen(false);
    setToolsOpen(false);
  };

  const openTools = () => {
    if (toolsTimeout.current) clearTimeout(toolsTimeout.current);
    setToolsOpen(true);
  };

  const closeTools = () => {
    toolsTimeout.current = setTimeout(() => setToolsOpen(false), 150);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_1px_12px_rgba(11,31,58,0.08)]"
          : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Logo size="md" />

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-1">
            {mainLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={closeNavigation}
                className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors rounded-md hover:bg-background-100 whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </a>
            ))}

            {/* Tools dropdown */}
            <div
              className="relative"
              onMouseEnter={openTools}
              onMouseLeave={closeTools}
            >
              <a
                href="/narzedzia"
                onClick={closeNavigation}
                className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors rounded-md hover:bg-background-100 whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                Narzędzia
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${toolsOpen ? 'rotate-180' : ''}`} />
              </a>
              {toolsOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl border border-background-200 shadow-lg py-2 z-50">
                  {toolsLinks.map((link) => (
                    <a
                      key={link.path}
                      href={link.path}
                      onClick={closeNavigation}
                      className="w-full text-left px-4 py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {link.label}
                    </a>
                  ))}
                  <div className="border-t border-background-100 mt-1 pt-1">
                    <a
                      href="/narzedzia"
                      onClick={closeNavigation}
                      className="w-full text-left px-4 py-2.5 text-sm font-semibold text-primary-600 hover:text-primary-500 hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Wszystkie narzędzia →
                    </a>
                  </div>
                </div>
              )}
            </div>

            {otherLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={closeNavigation}
                className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors rounded-md hover:bg-background-100 whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </a>
            ))}

            <a
              href="tel:698084044"
              className="ml-3 px-5 py-2.5 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors shadow-sm whitespace-nowrap cursor-pointer"
            >
              Zadzwoń
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Zamknij menu" : "Otwórz menu"}
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`} />
          </button>
        </div>
      </nav>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-background-200 shadow-lg">
          <div className="px-4 py-4 flex flex-col gap-1">
            {mainLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={closeNavigation}
                className="text-left px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </a>
            ))}
            <div className="px-4 py-2 text-xs font-bold text-foreground-400 uppercase tracking-wider">
              Narzędzia
            </div>
            {toolsLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={closeNavigation}
                className="text-left px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </a>
            ))}
            {otherLinks.map((link) => (
              <a
                key={link.path}
                href={link.path}
                onClick={closeNavigation}
                className="text-left px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </a>
            ))}
            <a
              href="tel:698084044"
              className="mt-2 inline-flex justify-center px-5 py-3 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Zadzwoń
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
